import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, Unplug } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { MessengerPageSummary } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { MessengerPendingPagesPanel } from '@/Domain/components/Integration/Messenger/MessengerPendingPagesPanel';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { META_REDIRECT_URI } from '@/Domain/components/Integration/metaOAuth';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import {
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { MessengerIcon } from '@/Domain/components/icons/Messenger.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';
import { getCurrentScenarioId } from '@/context';

const FACEBOOK_INTEGRATION_NAME = 'facebook' as const;
const FACEBOOK_QUERY_KEY = [QueryKey.integration.get, FACEBOOK_INTEGRATION_NAME] as const;
const MESSENGER_PAGES_QUERY_KEY = [QueryKey.integration.messengerPages] as const;

const FACEBOOK_OAUTH_BASE = 'https://www.facebook.com/v18.0/dialog/oauth';
const FACEBOOK_APP_ID = '775209690952444';
// Exactly what Meta grants. pages_read_engagement and business_management are Standard
// access only, so Meta offers them to app-role users and never to customers — asking put
// staff on a different flow than every merchant (docs/whatsapp-embedded-signup-followups.md).
const FACEBOOK_SCOPES = [
  'email',
  'pages_show_list',
  'pages_messaging',
  'pages_manage_metadata',
  'public_profile',
].join(',');
const POPUP_FEATURES = 'width=600,height=800';
const POPUP_POLL_INTERVAL_MS = 500;
// COOP severs the popup handle, so `popup.closed` reads true immediately: poll the backend instead.
const MESSENGER_CONNECT_POLL_INTERVAL_MS = 3_000;
const MESSENGER_CONNECT_POLL_TIMEOUT_MS = 120_000;

interface FacebookIntegrationParams {
  facebook_bot_active?: boolean;
  facebook_page_id?: string | null;
  facebook_page_name?: string | null;
  facebook_token_expires_at?: string | null;
  facebook_connected_at?: string | null;
  facebook_app_secret?: string | null;
  facebook_access_token?: string | null;
  facebook_verify_token?: string | null;
}

interface FacebookIntegrationResponse {
  name: 'facebook';
  params: FacebookIntegrationParams;
}

interface MessengerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function buildOAuthUrl(scenarioId: string): string {
  const state = encodeURIComponent(JSON.stringify({ scenario_id: scenarioId, type: 'messenger' }));
  const params = [
    `client_id=${FACEBOOK_APP_ID}`,
    `redirect_uri=${encodeURIComponent(META_REDIRECT_URI)}`,
    `scope=${FACEBOOK_SCOPES}`,
    `state=${state}`,
    'response_type=code',
    'display=popup',
    // re-prompt for any permission the user previously declined
    'auth_type=rerequest',
  ].join('&');
  return `${FACEBOOK_OAUTH_BASE}?${params}`;
}

const formatDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

export const MessengerSheet = ({ open, onOpenChange }: MessengerSheetProps) => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [connectStartedAt, setConnectStartedAt] = useState<number | null>(null);
  const [connectTimedOut, setConnectTimedOut] = useState(false);
  const [isCheckingAfterDismiss, setIsCheckingAfterDismiss] = useState(false);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const selectionShownRef = useRef<string | null>(null);
  // Baseline at popup-open time: a re-auth starts with Pages already staged, so only a change means the connect landed.
  const stagedKeyAtConnectRef = useRef<string | null>(null);
  const connectedPageIdAtConnectRef = useRef<string | null>(null);
  // Read after an await, so it has to be the live value rather than the captured one.
  const openRef = useRef(open);
  openRef.current = open;
  // Read by the on-close effect, which must not re-run when either of these changes.
  const connectStartedAtRef = useRef<number | null>(null);
  connectStartedAtRef.current = connectStartedAt;
  const isCheckingAfterDismissRef = useRef(false);
  isCheckingAfterDismissRef.current = isCheckingAfterDismiss;

  const clearPopupPoll = useCallback(() => {
    if (popupPollRef.current !== null) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearPopupPoll();
    },
    [clearPopupPoll],
  );

  const { data, isLoading, refetch } = useQuery<FacebookIntegrationResponse>({
    queryKey: FACEBOOK_QUERY_KEY,
    enabled: open,
  });

  const {
    data: candidatePages,
    refetch: refetchPages,
    isLoading: isLoadingPages,
    isFetching: isFetchingPages,
  } = useQuery<MessengerPageSummary[]>({
    queryKey: MESSENGER_PAGES_QUERY_KEY,
    enabled: open,
  });

  const pageId = data?.params?.facebook_page_id ?? null;
  const pageName = data?.params?.facebook_page_name ?? null;
  const tokenExpiresAt = data?.params?.facebook_token_expires_at ?? null;
  const connectedAt = data?.params?.facebook_connected_at ?? null;
  // Connected = a Page is linked; Enabled = the bot is currently replying.
  const isConnected = Boolean(pageId);
  const isEnabled = Boolean(data?.params?.facebook_bot_active);
  // A non-empty candidate list means OAuth landed but no Page has been confirmed yet.
  const pendingPages = candidatePages ?? [];
  const isAwaitingSelection = pendingPages.length > 0;
  const isConnectInFlight = connectStartedAt !== null;
  // Sorted so a reordering from Meta doesn't read as a different set of Pages.
  const stagedKey = pendingPages
    .map((page) => page.page_id)
    .sort()
    .join(',');

  // Held in a ref so the interval below keys only on its own timing inputs.
  const refetchAllRef = useRef<() => void>(() => {});
  useEffect(() => {
    refetchAllRef.current = () => {
      refetch();
      refetchPages();
    };
  }, [refetch, refetchPages]);

  // "Some Pages exist" cannot mean "the connect landed" — a re-auth starts that way.
  useEffect(() => {
    if (connectStartedAt === null) return;
    if (
      stagedKey === stagedKeyAtConnectRef.current &&
      pageId === connectedPageIdAtConnectRef.current
    ) {
      return;
    }
    setConnectStartedAt(null);
  }, [connectStartedAt, pageId, stagedKey]);

  // The popup can never tell us it finished (COOP nulls window.opener), so polling is the only signal.
  useEffect(() => {
    if (!open || connectStartedAt === null) return;
    const poll = setInterval(() => {
      if (Date.now() - connectStartedAt >= MESSENGER_CONNECT_POLL_TIMEOUT_MS) {
        setConnectStartedAt(null);
        setConnectTimedOut(true);
        return;
      }
      refetchAllRef.current();
    }, MESSENGER_CONNECT_POLL_INTERVAL_MS);
    return () => clearInterval(poll);
  }, [connectStartedAt, open]);

  // The Integrations page never unmounts this sheet, so the interval must stop on close.
  useEffect(() => {
    if (open) return;
    // Keep the "we did not hear back" notice for the next open; handleConnect clears it on retry.
    // An explicit dismiss is not a walk-away — handleStopWaiting owns that verdict and drops it
    // when the sheet closes inside its round trip, so don't race it with a second one.
    if (connectStartedAtRef.current !== null && !isCheckingAfterDismissRef.current) {
      capture(PostHogEventEnum.MessengerConnectAbandoned, {
        waited_ms: Date.now() - connectStartedAtRef.current,
      });
      setConnectTimedOut(true);
    }
    setConnectStartedAt(null);
    setIsCheckingAfterDismiss(false);
    clearPopupPoll();
    // A disabled useQuery stops re-rendering, so reopening is the only impression signal.
    selectionShownRef.current = null;
  }, [capture, clearPopupPoll, open]);

  useEffect(() => {
    // The grid fetches this same key, so a closed sheet would report an impression nobody saw.
    if (!open) return;
    if (!isAwaitingSelection) {
      // Cleared so a cancel-then-restage of the same Pages counts as a fresh impression.
      selectionShownRef.current = null;
      return;
    }
    if (selectionShownRef.current === stagedKey) return;
    selectionShownRef.current = stagedKey;
    capture(PostHogEventEnum.MessengerPageSelectionShown, {
      staged_page_count: pendingPages.length,
    });
  }, [capture, isAwaitingSelection, open, pendingPages.length, stagedKey]);

  // Pause/resume replies without disconnecting the Page.
  const toggleMutation = useMutation<
    FacebookIntegrationResponse,
    Error,
    { name: typeof FACEBOOK_INTEGRATION_NAME; params: { facebook_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response, variables) => {
      queryClient.setQueryData<FacebookIntegrationResponse>(FACEBOOK_QUERY_KEY, (prev) => ({
        name: FACEBOOK_INTEGRATION_NAME,
        params: { ...(prev?.params ?? {}), ...response.params },
      }));
      const name = t('integration:messenger.title');
      toast.success(
        variables.params.facebook_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: () => {
      toast.error(t('integration:messenger.update_failed'));
    },
  });

  const handleToggleActive = useCallback(
    (checked: boolean) => {
      toggleMutation.mutate({
        name: FACEBOOK_INTEGRATION_NAME,
        params: { facebook_bot_active: checked },
      });
    },
    [toggleMutation],
  );

  const disconnectMutation = useMutation({
    mutationKey: [QueryKey.integration.facebookMessengerDelete],
    onSuccess: () => {
      queryClient.setQueryData<FacebookIntegrationResponse>(FACEBOOK_QUERY_KEY, {
        name: FACEBOOK_INTEGRATION_NAME,
        params: {},
      });
      // Disconnect drops the whole row, so a left-behind candidate could only 404.
      queryClient.setQueryData<MessengerPageSummary[]>(MESSENGER_PAGES_QUERY_KEY, []);
      setConnectStartedAt(null);
      setConnectTimedOut(false);
      setConfirmOpen(false);
    },
    onError: () => {
      toast.error(t('integration:messenger.disconnect_failed'));
    },
  });

  const selectPageMutation = useMutation<void, Error, string>({
    mutationKey: [QueryKey.integration.messengerPageSelect],
    onSuccess: (_result, selectedPageId) => {
      // Written before the candidates go, or the sheet renders Connect and "Not connected"
      // under a success toast for as long as the integration GET below takes.
      const selected = pendingPages.find((page) => page.page_id === selectedPageId);
      queryClient.setQueryData<FacebookIntegrationResponse>(FACEBOOK_QUERY_KEY, (prev) => ({
        name: FACEBOOK_INTEGRATION_NAME,
        params: {
          ...(prev?.params ?? {}),
          facebook_page_id: selectedPageId,
          facebook_page_name: selected?.page_name || null,
        },
      }));
      queryClient.setQueryData<MessengerPageSummary[]>(MESSENGER_PAGES_QUERY_KEY, []);
      setConnectStartedAt(null);
      setConnectTimedOut(false);
      refetch();
      // OAuth alone only staged Pages, so this pick is the first moment Messenger is live.
      trackIntegrationConnectSucceeded(capture, 'messenger');
      toast.success(t('integration:messenger.page_connected'));
    },
    onError: () => {
      toast.error(t('integration:messenger.page_select_failed'));
    },
  });

  const handleSelectPage = useCallback(
    (page: MessengerPageSummary) => {
      selectPageMutation.mutate(page.page_id);
    },
    [selectPageMutation],
  );

  const confirmingPageId = selectPageMutation.isPending
    ? (selectPageMutation.variables ?? null)
    : null;

  const handleConnect = useCallback(() => {
    const scenarioId = getCurrentScenarioId();
    const url = buildOAuthUrl(scenarioId);
    capture(PostHogEventEnum.MessengerOauthStarted);
    trackIntegrationConnectStarted(capture, 'messenger');
    const popup = window.open(url, 'messenger_oauth', POPUP_FEATURES);
    if (!popup) {
      trackIntegrationConnectFailed(capture, 'messenger', 'popup_blocked');
      toast.error(t('integration:messenger.popup_blocked'));
      return;
    }
    stagedKeyAtConnectRef.current = stagedKey;
    connectedPageIdAtConnectRef.current = pageId;
    setConnectStartedAt(Date.now());
    setConnectTimedOut(false);

    clearPopupPoll();
    popupPollRef.current = setInterval(() => {
      // Guard against the host going away mid-poll (test teardown, page nav).
      if (typeof window === 'undefined' || !popup) {
        clearPopupPoll();
        return;
      }
      if (popup.closed) {
        clearPopupPoll();
        refetch();
        refetchPages();
      }
    }, POPUP_POLL_INTERVAL_MS);
  }, [capture, clearPopupPoll, pageId, refetch, refetchPages, stagedKey, t]);

  // Without this the lock only lifts on the 2 min timeout when nothing changed server-side.
  const handleStopWaiting = useCallback(async () => {
    clearPopupPoll();
    setIsCheckingAfterDismiss(true);
    // Unlocking before these land would re-enable Confirm against the stale list.
    const [integration, pages] = await Promise.all([refetch(), refetchPages()]);
    setIsCheckingAfterDismiss(false);
    // The sheet can be closed inside that round trip, and its reset has already run.
    if (!openRef.current) return;
    const stagedNow = (pages.data ?? [])
      .map((page) => page.page_id)
      .sort()
      .join(',');
    const connectedNow = integration.data?.params?.facebook_page_id ?? null;
    setConnectStartedAt(null);
    // Dismissing early is invited by the copy, and stopping the poll silently would put the
    // merchant back in front of a "not connected" sheet. Say Facebook never answered instead.
    setConnectTimedOut(
      stagedNow === stagedKeyAtConnectRef.current &&
        connectedNow === connectedPageIdAtConnectRef.current,
    );
  }, [clearPopupPoll, refetch, refetchPages]);

  const handleDisconnect = useCallback(() => {
    disconnectMutation.mutate();
  }, [disconnectMutation]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        data-testid="messenger-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <MessengerIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="messenger-title">
                  {t('integration:messenger.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} pending={isAwaitingSelection} />
                <IntegrationEnabledBadge enabled={isEnabled} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="messenger-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          {/* Waits on the Pages query too, or a pending merchant briefly sees an enabled Connect. */}
          {isLoading || isLoadingPages ? (
            <div
              className="flex items-center justify-center py-12 text-muted-foreground"
              data-testid="messenger-loading"
            >
              <Spinner className="size-5" />
            </div>
          ) : (
            <div className="space-y-6" data-testid="messenger-content">
              {/* Above the panel it locks: on a long list the reason would be off-screen. */}
              {isConnectInFlight ? (
                <Alert variant="info" data-testid="messenger-connect-waiting">
                  <Spinner className="size-4" />
                  <AlertTitle>{t('integration:messenger.select_page.waiting_title')}</AlertTitle>
                  <AlertDescription className="flex flex-col items-start gap-2 text-xs">
                    <p>{t('integration:messenger.select_page.waiting_description')}</p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={handleStopWaiting}
                      disabled={isCheckingAfterDismiss}
                      data-testid="messenger-connect-waiting-dismiss"
                    >
                      {isCheckingAfterDismiss ? <Spinner className="size-3" /> : null}
                      {t('integration:messenger.select_page.waiting_dismiss')}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* Above the introduction on purpose: under the overview copy it read as more copy. */}
              {isAwaitingSelection ? (
                <div data-testid="messenger-page-selection-section">
                  <MessengerPendingPagesPanel
                    pages={pendingPages}
                    onConfirm={handleSelectPage}
                    onConnectDifferentAccount={handleConnect}
                    onCancel={isConnected ? undefined : handleDisconnect}
                    isConnected={isConnected}
                    confirmingPageId={confirmingPageId}
                    isCancelling={disconnectMutation.isPending}
                    isConnecting={isConnectInFlight}
                  />
                </div>
              ) : null}

              {connectTimedOut && !isAwaitingSelection && !isConnected ? (
                <Alert variant="warning" data-testid="messenger-connect-timeout">
                  <AlertDescription className="text-xs">
                    {t('integration:messenger.select_page.waiting_timeout')}
                  </AlertDescription>
                </Alert>
              ) : null}

              <IntegrationIntroduction
                description={t('integration:messenger.overview')}
                prerequisites={
                  t('integration:messenger.prerequisites', { returnObjects: true }) as string[]
                }
                docsHref="https://docs.quickchat.ai/channels/facebook-messenger"
              />

              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="messenger-connect-enable-section"
              >
                {isConnected ? (
                  <IntegrationConnectionCard
                    data-testid="messenger-connection-card"
                    details={[
                      {
                        label: t('integration:messenger.page'),
                        value: pageName ?? pageId ?? '',
                        'data-testid': 'messenger-connected-label',
                      },
                      {
                        label: t('integration:messenger.page_id'),
                        value: pageId ?? '',
                        'data-testid': 'messenger-page-id',
                      },
                      {
                        label: t('integration:messenger.access_expires'),
                        value: formatDate(tokenExpiresAt),
                        'data-testid': 'messenger-token-expires',
                      },
                      {
                        label: t('integration:messenger.connected_since'),
                        value: formatDate(connectedAt),
                        'data-testid': 'messenger-connected-since',
                      },
                    ]}
                    actions={
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={handleConnect}
                          disabled={isConnectInFlight}
                          data-testid="messenger-switch-page-button"
                        >
                          <RefreshCw className="size-4" />
                          {t('integrations:reconnect')}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfirmOpen(true)}
                          disabled={disconnectMutation.isPending || isConnectInFlight}
                          data-testid="messenger-disconnect-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </>
                    }
                  />
                ) : isAwaitingSelection ? null : (
                  // Hidden while a pick is pending, and dead while the list is being re-read:
                  // a cached empty list keeps isLoadingPages false, so this is the last window
                  // in which a staged pick can still be mistaken for "not connected yet".
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={handleConnect}
                    disabled={isConnectInFlight || isFetchingPages}
                    data-testid="messenger-connect-button"
                  >
                    <Plug className="size-4" />
                    {t('integrations:connect', { name: 'Facebook Messenger' })}
                  </Button>
                )}
              </IntegrationSection>

              <IntegrationSection
                title={t('integrations:section.settings')}
                data-testid="messenger-settings-section"
              >
                <IntegrationToggleField
                  id="messenger-enable-switch"
                  label={t('integrations:enable', { name: 'Messenger' })}
                  description={
                    isConnected ? undefined : t('integration:messenger.enable_locked_hint')
                  }
                  checked={isEnabled}
                  onCheckedChange={handleToggleActive}
                  disabled={!isConnected || toggleMutation.isPending}
                  data-testid="messenger-enable-switch"
                />
              </IntegrationSection>
            </div>
          )}
        </div>

        <AlertDialogPrimitive.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="messenger-disconnect-dialog"
            >
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('integration:messenger.disconnect_confirm.title')}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogDescription>
                {t('integration:messenger.disconnect_confirm.description')}
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogPrimitive.Cancel asChild>
                  <Button
                    size="md"
                    variant="secondary"
                    data-testid="messenger-disconnect-cancel-button"
                  >
                    {t('integration:messenger.disconnect_confirm.cancel')}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleDisconnect}
                  disabled={disconnectMutation.isPending}
                  data-testid="messenger-disconnect-confirm-button"
                >
                  {disconnectMutation.isPending ? <Spinner className="size-4" /> : null}
                  {t('integration:messenger.disconnect_confirm.confirm')}
                </Button>
              </AlertDialogFooter>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
